import {useEffect} from 'react';
import {gql, NetworkStatus, useQuery} from '@apollo/client';

import {tailMatch} from './helpers';
import {useState} from './reducer';
import {Txn} from './types';

interface Vars {
	after?: string;
}

interface Result {
	auth: {
		user: {
			txns: {
				edges: {
					node: Txn,
				}[]
				pageInfo: {
					hasNextPage: boolean
					lastCursor: string
				}
			}
		}
	}
}

const getTxns = gql`
query ($after: String) {
	auth {
		id
		user {
			id
			txns(page: {after: $after}) {
				edges {
					node {
						id
						details {
							reason
							created {
								unix
							}
						}
						items {
							product {
								name: shortName
							}
							quantity
						}
						props {
							comment: publicComment
						}
					}
				}
				pageInfo {
					hasNextPage
					lastCursor
				}
			}
		}
	}
}`;

export const useTxns = () => {
	const [txns, dispatch] = useState();

	// Generate a query data and results that we can use
	const result = useQuery<Result, Vars>(getTxns, {
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	});

	// Check our query results and merge txns from multiple results
	useEffect(() => {
		if (result.data?.auth.user?.txns) {
			const next = result.data.auth.user?.txns.edges.map(({node}) => node);

			// If after is unset, always set to our current results
			if (!result.variables?.after) {
				if (!tailMatch(txns, next, true)) {
					dispatch({txns: next, type: 'SET'});
				}
				return;
			}

			// If we don't have current and next... ignore?
			if (!(txns.length > 0 && next.length > 0)) {
				return;
			}

			// Check if we have a mini overlap
			if (txns[txns.length - 1].id === next[0].id) {
				dispatch({txns: next, type: 'CHAIN'});
				return;
			}

			// Check if we have a full overlap
			if (tailMatch(txns, next)) {
				return;
			}

			// Uh-oh, dispatch a full reset
			result.refetch({after: undefined});
		}
	}, [result, txns]);

	const hasMore = Boolean(result.data?.auth.user?.txns.pageInfo.hasNextPage);
	const fetchMore = !hasMore ? undefined : () => {
		result.refetch({
			after: `${txns.length - 2}`,
		});
	};

	return {
		fetchMore,
		loading: result.networkStatus !== NetworkStatus.ready,
		txns,
	};
};
